import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const landingSelector = '[data-ac-landing] form[action*="/cart/add"]';

async function addLandingProduct(event) {
  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement)) return;

  event.preventDefault();

  const submitButton = event.submitter instanceof HTMLButtonElement
    ? event.submitter
    : form.querySelector('button[name="add"]');
  if (submitButton?.disabled || form.dataset.acCartBusy === 'true') return;

  const formData = new FormData(form);
  const variantId = Number(formData.get('id'));
  const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
  if (!Number.isSafeInteger(variantId) || variantId <= 0) return;

  form.dataset.acCartBusy = 'true';
  submitButton?.setAttribute('aria-busy', 'true');
  if (submitButton) submitButton.disabled = true;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : null))
    .filter(Boolean);
  const uniqueSectionIds = [...new Set(sectionIds)];
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const body = JSON.stringify({
      items: [{ id: variantId, quantity }],
      sections: uniqueSectionIds.join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add item');
    }

    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: result.sections,
        source: 'allerclip-landing',
        didError: false,
      },
    });

    requestAnimationFrame(() => {
      const drawer = document.querySelector('#cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    });
  } catch (error) {
    deferred.reject(error);
    form.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Unable to add item',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    delete form.dataset.acCartBusy;
    submitButton?.removeAttribute('aria-busy');
    if (submitButton) submitButton.disabled = false;
  }
}


function initializeLandingCartTrigger(scope = document) {
  const trigger = scope.querySelector('[data-ac-open-cart]');
  if (!(trigger instanceof HTMLAnchorElement) || trigger.dataset.acCartBound === 'true') return;
  trigger.dataset.acCartBound = 'true';
  trigger.addEventListener('click', (event) => {
    const drawer = document.querySelector('#cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open();
  });
}

function initializeLandingCartForms(scope = document) {
  scope.querySelectorAll(landingSelector).forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.acCartBound === 'true') return;
    form.dataset.acCartBound = 'true';
    form.addEventListener('submit', addLandingProduct);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { initializeLandingCartForms(); initializeLandingCartTrigger(); }, { once: true });
} else {
  initializeLandingCartForms();
  initializeLandingCartTrigger();
}

document.addEventListener('shopify:section:load', (event) => { initializeLandingCartForms(event.target); initializeLandingCartTrigger(event.target); });
